import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { DealFilter } from 'projects/semeris/src/features/deal-filter/models/deal-filter.model';
import { DealFilterService } from 'projects/semeris/src/features/deal-filter/services/deal-filter.service';
import { RootState } from 'projects/semeris/src/store/root-state.model';
import { EMPTY, from, Observable } from 'rxjs';
import { concatMap, filter, map, mergeMap, switchMap, takeUntil, toArray, withLatestFrom } from 'rxjs/operators';

import { DealFilterActions } from './deal-filter.actions';
import { DealFilterSelectors } from './deal-filter.selectors';
import {
  WebSocketEntities,
  WebSocketReloadEvent,
} from '../../features/shared/models/web-socket/web-socket.models';
import {
  createWebSocketEffect,
  createWebSocketWithWarningEffect,
} from '../../features/shared/services/web-socket/create-web-socket-effect';
import { WebSocketService } from '../../features/shared/services/web-socket/web-socket.service';
import {
  createNotificationEffect,
  PushNotificationRecord,
} from '../../features/shared/utils/create-notification-effect';
import { isTransientRequestFailure } from '../../features/shared/utils/is-transient-request-failure';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { AppActions } from '../app/app.actions';
import { UserProfileActions } from '../user-profile/user-profile.actions';

/** Each save re-resolves its portfolio server-side, so a bulk edit must not fire them all at once. */
const MEMBERSHIP_SAVE_CONCURRENCY = 4;

/** Idempotent, so it does not matter whether the optimistic patch has already been applied. */
function withDeal(dealFilter: DealFilter | undefined, transactionId: string, add: boolean): DealFilter | undefined {
  if (!dealFilter) {
    return undefined;
  }

  const dealList = dealFilter.dealList ?? [];

  if (!add) {
    return { ...dealFilter, dealList: dealList.filter((id) => id !== transactionId) };
  }

  return dealList.includes(transactionId) ? dealFilter : { ...dealFilter, dealList: [...dealList, transactionId] };
}

@Injectable()
export class DealFilterEffects {
  private service = inject(DealFilterService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);
  private readonly webSocketService = inject(WebSocketService);

  private readonly notifications: PushNotificationRecord = {
    [DealFilterActions.CreateOrUpdateDealFilterSuccess.type]: {
      message: 'Portfolio saved',
      isSuccess: true,
    },
    [DealFilterActions.CreateOrUpdateDealFilterFail.type]: {
      message: 'Failed to save the portfolio',
      isSuccess: false,
    },
    // The success toast is optimistic, so without this a rejected save reverts in silence.
    [DealFilterActions.SetDealMembershipFail.type]: {
      message: "Failed to change the deal's portfolios",
      isSuccess: false,
    },
  };

  loadListEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.LoadList),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.getAll().pipe(
          map((list) => DealFilterActions.LoadListSuccess({ list, withApply: action.withApply })),
          reportEffectError(DealFilterActions.LoadListFail)
        )
      )
    )
  );

  loadListApplyEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.LoadListSuccess),
      filter((action) => action.withApply),
      withLatestFrom(this.store),
      map(([action, store]) =>
        store.dealFilter.selectedFilterId
          ? DealFilterActions.UseFilter({ id: store.dealFilter.selectedFilterId })
          : DealFilterActions.UseDefaultFilter()
      )
    )
  );

  loadListSuccessEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DealFilterActions.CreateOrUpdateDealFilterSuccess),
        // A save is a scope change, not a destination: the id joins the query where we stand.
        map((action) =>
          this.router.navigate([], {
            queryParams: { dealFilterId: action.id, new: null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          })
        )
      ),
    { dispatch: false }
  );

  useDefaultFilterEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.UseDefaultFilter),
      withLatestFrom(
        this.store.select(DealFilterSelectors.selectedFilter),
        this.store.select(DealFilterSelectors.list)
      ),
      filter(([, dealFilter, list]) => !!dealFilter.id && list.some((f) => f.id === dealFilter.id)),
      map(([, dealFilter]) =>
        DealFilterActions.UseFilter({
          id: dealFilter.id,
          skipSave: true,
        })
      )
    )
  );

  useFilterSaveEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.UseFilter),
      filter((action) => !action.skipSave),
      map((action) =>
        UserProfileActions.UpdateAssetSetting({ setting: { selectedDealFilterId: action.id } })
      )
    )
  );

  clearSelectionSaveEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.ClearSelection, DealFilterActions.UseBasket),
      map(() => UserProfileActions.UpdateAssetSetting({ setting: { selectedDealFilterId: null } })),
    ),
  );

  useFilterQueryEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.UseFilter),
      filter((action) => !!action.id),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.getById(action.id).pipe(
          map((dealFilter) => DealFilterActions.UseFilterSuccess({ dealFilter })),
          reportEffectError(DealFilterActions.UseFilterFail)
        )
      )
    )
  );

  /**
   * The N portfolio saves for one membership edit, reported as a single success. The reducer has
   * already applied the edit, so a per-document success would be the only thing that could make
   * the checkboxes move again.
   */
  setDealMembership$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.SetDealMembership),
      withLatestFrom(this.store.select(DealFilterSelectors.filters)),
      // Serialised: two edits touching one portfolio must not race. Latency is invisible here,
      // because the optimistic patch has already answered the user.
      concatMap(([action, list]) => {
        const byId = new Map((list || []).map((dealFilter) => [dealFilter.id, dealFilter]));
        const saves = [
          ...action.add.map((id) => withDeal(byId.get(id), action.transactionId, true)),
          ...action.remove.map((id) => withDeal(byId.get(id), action.transactionId, false)),
        ].filter((dealFilter) => !!dealFilter);

        if (!saves.length) {
          return EMPTY;
        }

        return from(saves).pipe(
          mergeMap((dealFilter) => this.service.update(dealFilter, true, false), MEMBERSHIP_SAVE_CONCURRENCY),
          toArray(),
          map((dealFilters) => DealFilterActions.SetDealMembershipSuccess({ dealFilters })),
          reportEffectError(DealFilterActions.SetDealMembershipFail)
        );
      })
    )
  );

  /**
   * A rejected save leaves the optimistic patch to undo. `withApply` re-reads the applied scope
   * too: the reload refreshes the list, and without it the applied copy keeps the failed edit.
   */
  setDealMembershipFail$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.SetDealMembershipFail),
      withLatestFrom(this.store.select(DealFilterSelectors.selectedFilterId)),
      map(([, selectedFilterId]) => DealFilterActions.LoadList({ withApply: !!selectedFilterId }))
    )
  );

  createDealFilter$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.CreateOrUpdateDealFilter),
      withLatestFrom(this.store),
      // `concatMap`, never `switchMap`: these are independent documents, and one membership edit
      // can save several at once. switchMap cancelled every request but the last.
      concatMap(([action, store]) => {
        const dealFilter = {
          ...action.dealFilter,
          displayName:
            action.dealFilter?.displayName ||
            this.service.generateDisplayName(
              action.dealFilter,
              store.manager.list,
              store.transaction.list
            ),
          sort: store.dealFilter.sort,
        };

        return (
          dealFilter.id
            ? this.service.update(dealFilter, true, false)
            : this.service.create(dealFilter)
        ).pipe(
          // The list as it is NOW, not the snapshot taken at dispatch: the reducer replaces the
          // whole list with this payload, so a stale one silently drops a sibling's save.
          withLatestFrom(this.store.select(DealFilterSelectors.filters)),
          map(([res, list]) => {
            return DealFilterActions.CreateOrUpdateDealFilterSuccess({
              dealFilters: [...(list || []).filter((df) => df.id !== res.id), res],
              id: res.id,
            });
          }),
          reportEffectError(DealFilterActions.CreateOrUpdateDealFilterFail)
        );
      })
    )
  );

  shareDealFilter$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.ShareDealFilter),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service
          .update(
            {
              id: action.dealFilter.id,
              shared: action.data.accessControl?.shared,
              sharedForEdit: action.data.accessControl?.sharedForEdit,
            } as any,
            false,
            true
          )
          .pipe(
            map((res) =>
              DealFilterActions.ShareDealFilterSuccess({
                dealFilter: {
                  ...action.dealFilter,
                  shared: action.data.accessControl?.shared,
                  sharedForEdit: action.data.accessControl?.sharedForEdit,
                },
              })
            ),
            reportEffectError(DealFilterActions.ShareDealFilterFail)
          )
      )
    )
  );

  shareTopicSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.ShareDealFilterSuccess),
      map((action) =>
        AppActions.PushNotification({
          message: action.dealFilter.shared ? 'Portfolio shared' : 'Portfolio share revoked',
          config: 'success',
        })
      )
    )
  );

  deleteFilterEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.DeleteFilter),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.delete(action.id).pipe(
          map(() =>
            DealFilterActions.DeleteFilterSuccess({
              dealFilters: [...(store.dealFilter.list || []).filter((df) => df.id !== action.id)],
              id: action.id,
            })
          ),
          reportEffectError(DealFilterActions.DeleteFilterFail)
        )
      )
    )
  );

  deleteFilterSuccessEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DealFilterActions.DeleteFilterSuccess),
        map(() => {
          // Deleting leaves no portfolio applied. It used to open a fresh editor, which read as
          // "now build another one" rather than "that one is gone".
          this.router.navigate([], {
            queryParams: { dealFilterId: null, new: null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        })
      ),
    { dispatch: false }
  );

  applyFilterEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.ApplyFilter),
      mergeMap((action) =>
        (action.preview ? this.service.preview(action.dealFilter) : this.service.getById(action.dealFilter.id)).pipe(
          map((dealFilter) =>
            DealFilterActions.ApplyFilterSuccess({
              dealFilter,
              resetSelectedFilter: action.resetSelectedFilter,
              preview: action.preview,
            }),
          ),
          reportEffectError(DealFilterActions.ApplyFilterFail),
          // mergeMap has nothing to cancel, so without this a late echo re-applies the abandoned filter.
          takeUntil(
            this.actions$.pipe(ofType(DealFilterActions.UseDefaultFilter, DealFilterActions.ClearSelection, DealFilterActions.UseBasket)),
          ),
        ),
      )
    )
  );

  notificationEffect$: Observable<Action> = createNotificationEffect(
    this.actions$,
    this.notifications
  );

  wsEffect$: Observable<Action> = createWebSocketWithWarningEffect(
    this.store,
    this.actions$,
    this.webSocketService,
    WebSocketEntities.DealFilter,
    (state) => DealFilterActions.LoadList({ withApply: true }),
    WebSocketReloadEvent.DealFilterReload
  );

  wsListEffect$: Observable<Action> = createWebSocketEffect(
    this.store,
    this.actions$,
    this.webSocketService,
    WebSocketEntities.DealFilter,
    () => DealFilterActions.LoadList({})
  );

  redirectOnUnauthorized$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealFilterActions.UseFilterFail),
      filter((action) => !isTransientRequestFailure(action.error)),
      map(() => AppActions.Unauthorized())
    )
  );
}
